import * as React from 'react';
import { ConfigurationPresent, RecordingsPresent } from '../../misc/api/DeviceLinkApi/VitaloJak2Messages';
import { DeviceTypes, DeviceType } from '../../misc/api/apiTypes';
import { ApiDevices, Device } from '../../misc/api/DeviceLinkApi/Devices';
import { ConnectCorrectDeviceMessage } from '../../misc/api/DeviceLinkApi/GenericMessages';
import { CheckForUpdates } from '../../misc/api/DeviceLinkApi/Updates';
import Alert, { AlertCategoryEnum } from '../../misc/components/Alert';
import AsyncActionButton from '../../misc/components/AsyncActionButton';
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { ExistingDataConflict } from './P230In2itiveEDiary/ExistingDataConflict';
import { P230In2itiveEDiaryCheckExistingData } from './P230In2itiveEDiary/P230In2itiveEDiaryCheckExistingData';
import { ResetSelectedDeviceAction, StartSessionWizardActions, UpdatingDeviceLinkEnum } from './StartSessionWizardReducer';
import { Subject } from "webportalclient";
import * as VitaloJak2Api from '../../misc/api/DeviceLinkApi/VitaloJak2';

interface DeviceSelectionProps {
    submitting: boolean;
    subject: Subject;
    selectedDeviceType: DeviceTypes;
    selectedDevice: Device;
    devicesConnected: Array<Device>;
    configurationWarning: string;
    onDevicesConnected: (devices: Array<Device>) => void;
    onDataError: (message: string, isWarning: boolean) => void;
    onSelectDevice: (device: Device) => void;
    onProceed: () => void;
    goTo: (step: string) => void;
    onBack: () => void;
    dispatch: React.Dispatch<StartSessionWizardActions>; 
}

export const DeviceSelection = ({ goTo, submitting, subject, selectedDeviceType, selectedDevice, devicesConnected, configurationWarning, onDevicesConnected, onDataError, onSelectDevice, onProceed, onBack, dispatch }: DeviceSelectionProps) => {
    const [searching, setSearching] = React.useState(false);
    const [deviceLinkStatus, setDeviceLinkStatus] = React.useState<UpdatingDeviceLinkEnum>(UpdatingDeviceLinkEnum.NotUpdating);
    const [deviceConnectionError, setDeviceConnectionError] = React.useState<string>();

    const fetchDevices = async (selectedDeviceType: DeviceTypes) => {
        if (selectedDeviceType === null)
            return;

        let devices = [];
        try {
            onDevicesConnected([]);
            setSearching(true);
            devices = await ApiDevices();

            devices = devices.filter(x => x.type === selectedDeviceType);

            if (devices.length === 0) {
                setSearching(false);
                return setDeviceConnectionError(ConnectCorrectDeviceMessage);
            }
        }
        catch (err) {
            goTo("pair-device-link");
            setSearching(false);
        }

        try {
            await handleDevicesConnected(devices);
            setDeviceConnectionError(undefined);
            setSearching(false);
        }
        catch (err) {
            onDataError("Failed to connect to device", false);
            goTo("error");
            setSearching(false);
        }
    }

    React.useEffect(() => {
        const checkForDeviceLinkUpdate = async () => {
            await CheckForUpdates(
                () => setDeviceLinkStatus(UpdatingDeviceLinkEnum.Updating),
                () => setDeviceLinkStatus(UpdatingDeviceLinkEnum.NotUpdating),
                () => setDeviceLinkStatus(UpdatingDeviceLinkEnum.FailedUpdate))
        }

        checkForDeviceLinkUpdate();
        fetchDevices(selectedDeviceType);
    }, [selectedDeviceType]);

    React.useEffect(() => {
        dispatch(ResetSelectedDeviceAction.create());
    },[])

    const handleDevicesConnected = async (devices: Device[]) => {
        const onP230In2itive = async (device: Device) => {
            const result = await P230In2itiveEDiaryCheckExistingData(device,
                (message, isWarning) => {
                    onDataError(message, isWarning);
                });
            if (result) {
                // TODO
            }
        }

        const onVitaloJAK2 = async (device: Device) => {
            const dataCheck = await VitaloJak2Api.ApiCheckData(device.id);
            const configCheck = await VitaloJak2Api.ApiGetSubject(device.id);

            // RecordingsPresent warning to take precedence over ConfiguurationPresent warning
            if (dataCheck.hasRecordings) {
                onDataError(RecordingsPresent, true);
                return;
            }

            if (configCheck.subjectId.trim().length > 0) {
                onDataError(ConfigurationPresent, true);
                return;
            }
        }

        for (const device of devices) {
            switch (device.type) {
                case DeviceTypes.VitaloJAK1:
                    //await onVitaloJAK1();
                    break;
                case DeviceTypes.VitaloJAK2:
                    await onVitaloJAK2(device);
                    break;
                case DeviceTypes.StatStripXpress2:
                    //await onStatStripXpress2();
                    break;
                case DeviceTypes.In2itiveP230EDiary:
                    await onP230In2itive(device);
                    break;
            }
        }

        onDevicesConnected(devices);
    }

    const handleDeviceSelectionChange = async (device: Device) => {
        onSelectDevice(device);
    }

    const DeviceSelectionItem = ({ id, icon, onChange, isChecked, children }) => {
        return (
            <li>
                <input id={id} type="radio" name="action" value={id} checked={isChecked} onChange={(e) => onChange(e.target.checked)} />
                <label htmlFor={id}>
                    <span className="posh-select-icon">
                        <i className={`fa ${icon}`} aria-hidden="true" />
                    </span>
                    <span className="posh-select-body">
                        {children}
                    </span>
                </label>
            </li>
        );
    }

    const renderDeviceSelection = () => {
        if (devicesConnected.length === 0) {
            return;
        }

        return (
            <>
                <p style={{ marginBottom: 15 + 'px' }} className="text-muted">Select which device you wish to configure</p>
                <ul className="posh-select">
                    {devicesConnected.map((device) => (
                        <DeviceSelectionItem
                            key={`Device${device.id}`}
                            id={device.id}
                            icon="fas fa-tablet" isChecked={selectedDevice === device}
                            onChange={() => handleDeviceSelectionChange(device)}
                        >
                            <>
                                <span className="posh-select-title">
                                    {device.name}
                                    <span className="pull-right">
                                        { device.type !== DeviceTypes.In2itiveP230EDiary ? configurationWarning ? <ExistingDataConflict configurationWarning={configurationWarning} /> : null : null }
                                    </span>
                                </span>
                                
                                {`${device.connection} • ${device.serial}`}
                            </>
                        </DeviceSelectionItem>
                    ))}
                </ul>
            </>
        );
    }

    const updatingDeviceLinkContent = () => {
        if (deviceLinkStatus === UpdatingDeviceLinkEnum.Updating) {
            return <LoadingWithMessage message="Updating DeviceLink Software..." />
        } else if (deviceLinkStatus === UpdatingDeviceLinkEnum.FailedUpdate) {
            return (
                <Alert category={AlertCategoryEnum.Danger} show={true}>
                    Failed to update DeviceLink Software
                </Alert>
            );
        }
    }

    const renderContent = () => {
        if (deviceLinkStatus !== UpdatingDeviceLinkEnum.NotUpdating) {
            return updatingDeviceLinkContent();
        } else if (searching) {
            return (<LoadingWithMessage message="Connecting to Devices..." />);
        } else {
            return (
                <>
                    <h2 className="text-primary">Device Selection</h2>
                    <Alert show={deviceConnectionError !== undefined} category={AlertCategoryEnum.Danger}>{deviceConnectionError}</Alert>
                    {renderDeviceSelection()}
                </>
            );
        }
    }

    return (
        <>
            <div className="modal-body">
                {renderContent()}
            </div>
            <div className="modal-footer">
                {devicesConnected.length > 0 ? <AsyncActionButton className="btn btn-primary" isSubmitting={submitting} disabled={!selectedDevice || submitting} onClick={onProceed}>Select Device</AsyncActionButton> : null}
                {devicesConnected.length === 0 ? <button className="btn btn-primary" onClick={() => fetchDevices(selectedDeviceType)}>Retry</button> : null}
                
                <button className="btn btn-primary btn-outline" onClick={onBack}>Back</button>
            </div>
        </>
    );
}
